export const plans = ['current', 'OBBBA_2026'] as const;

export type PlanId = (typeof plans)[number];

export interface Plan {
	id: PlanId;
	name: string;
	shortName: string;
	year: number;
}

export interface TaxPlan {
	plan: Plan;
	results: {
		gross_income: number;
		employee_payroll_tax: number;
		employer_payroll_tax: number;
		ordinary_income_after_401k: number;
		agi: number;
		taxable_income: number;
		deduction_type: string;
		deductions: number;
		standard_ded_for_display?: number;
		additional_standard_deduction?: number;
		salt_ded_for_display?: number;
		pease_limitation_for_display?: number;
		itemized_deductions_haircut?: number;
		charitable_ded_for_display?: number;
		senior_ded?: number;
		ot_income_ded?: number;
		auto_loan_interest_ded?: number;
		tips_income_ded?: number;
		mortgage_int_ded_for_display?: number;
		med_exp_ded_for_display?: number;
		other_itemized_ded_for_display?: number;
		personal_exemption_amt: number;
		pease_limitation_amt: number;
		qbi_ded: number;
		income_tax_before_credits: number;
		qualified_income_tax: number;
		selected_tax_before_credits: number;
		amt_taxable_income: number;
		amt: number;
		income_tax_before_credits_with_amt: number;
		ctc: number;
		actc: number;
		eitc: number;
		dep_credit: number;
		income_tax_after_nonrefundable_credits: number;
		medicare_surtax: number;
		niit: number;
		sched_se_tax: number;
		income_tax_after_other_taxes: number;
		income_tax_after_credits: number;
		total_payroll_tax: number;
		cash_income: number;
		tax_burden: number;
		tax_wedge: number;
		take_home_pay: number;
		avg_effective_tax_rate: number;
		avg_effective_tax_rate_wo_payroll: number;
		marginal_income_tax_rate: number;
		marginal_business_income_tax_rate: number;
	};
}

export interface Profile {
	description: {
		name: string;
		filingData: string;
		id: string;
		income: number;
		tooltip: string;
	};
	taxes: TaxPlan[];
}

const profiles = [
	{
		description: {
			name: 'James',
			filingData: 'Single, No Kids',
			id: 'james',
			income: 30000,
			tooltip: 'This taxpayer takes the standard deduction and had $2,600 of pre-tax 401(k) contributions.'
		},
		taxes: [
			{
				plan: {
					id: 'current',
					name: 'Pre-OBBBA Law (TCJA Expires)',
					shortName: 'Pre-OBBBA (TCJA Expires)',
					year: 2026
				},
				results: {
					gross_income: 30000,
					employee_payroll_tax: 2295,
					employer_payroll_tax: 2295,
					ordinary_income_after_401k: 27400,
					agi: 27400,
					taxable_income: 13800,
					deduction_type: 'standard',
					deductions: 8300,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 8300,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 5300,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 1462.5,
					qualified_income_tax: 1462.5,
					selected_tax_before_credits: 1462.5,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 1462.5,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 1462.5,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 1462.5,
					income_tax_after_credits: 1462.5,
					total_payroll_tax: 4590,
					cash_income: 32295,
					tax_burden: 3757.5,
					tax_wedge: 6052.5,
					take_home_pay: 26242.5,
					avg_effective_tax_rate: 0.18741291221551323,
					avg_effective_tax_rate_wo_payroll: 0.04875,
					marginal_income_tax_rate: 0.2265,
					marginal_business_income_tax_rate: 0.28069950000000027
				}
			},
			{
				plan: {
					id: 'OBBBA_2026',
					name: 'One Big Beautiful Bill Act',
					shortName: 'OBBBA',
					year: 2026
				},
				results: {
					gross_income: 30000,
					employee_payroll_tax: 2295,
					employer_payroll_tax: 2295,
					ordinary_income_after_401k: 27400,
					agi: 27400,
					taxable_income: 6300,
					deduction_type: 'standard',
					deductions: 21100,
					senior_ded: 0,
					ot_income_ded: 5000,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 16100,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 0,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 630,
					qualified_income_tax: 630,
					selected_tax_before_credits: 630,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 630,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 630,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 630,
					income_tax_after_credits: 630,
					total_payroll_tax: 4590,
					cash_income: 32295,
					tax_burden: 2925,
					tax_wedge: 5220,
					take_home_pay: 27075,
					avg_effective_tax_rate: 0.16163492800743148,
					avg_effective_tax_rate_wo_payroll: 0.021,
					marginal_income_tax_rate: 0.1765,
					marginal_business_income_tax_rate: 0.21423150000000005
				}
			}
		]
	},
	{
		description: {
			name: 'Jason',
			filingData: 'Single, 2 Kids',
			id: 'jason',
			income: 52000,
			tooltip: 'This taxpayer takes the standard deduction and had $4,000 of pre-tax 401(k) contributions.'
		},
		taxes: [
			{
				plan: {
					id: 'current',
					name: 'Pre-OBBBA Law (TCJA Expires)',
					shortName: 'Pre-OBBBA (TCJA Expires)',
					year: 2026
				},
				results: {
					gross_income: 52000,
					employee_payroll_tax: 3978,
					employer_payroll_tax: 3978,
					ordinary_income_after_401k: 48000,
					agi: 48000,
					taxable_income: 19950,
					deduction_type: 'standard',
					deductions: 12150,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 12150,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 15900,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 2125,
					qualified_income_tax: 2125,
					selected_tax_before_credits: 2125,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 2125,
					ctc: 2000,
					actc: 0,
					eitc: 1396.06,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 125,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 125,
					income_tax_after_credits: -1271.06,
					total_payroll_tax: 7956,
					cash_income: 55978,
					tax_burden: 2706.94,
					tax_wedge: 6684.9400000000005,
					take_home_pay: 49293.06,
					avg_effective_tax_rate: 0.11942084390296188,
					avg_effective_tax_rate_wo_payroll: -0.02444346153846154,
					marginal_income_tax_rate: 0.4371,
					marginal_business_income_tax_rate: 0.2806994999999999
				}
			},
			{
				plan: {
					id: 'OBBBA_2026',
					name: 'One Big Beautiful Bill Act',
					shortName: 'OBBBA',
					year: 2026
				},
				results: {
					gross_income: 52000,
					employee_payroll_tax: 3978,
					employer_payroll_tax: 3978,
					ordinary_income_after_401k: 48000,
					agi: 48000,
					taxable_income: 23850,
					deduction_type: 'standard',
					deductions: 24150,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 24150,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 0,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 2508,
					qualified_income_tax: 2508,
					selected_tax_before_credits: 2508,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 2508,
					ctc: 1000,
					actc: 3400,
					eitc: 1396.06,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 1508,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 1508,
					income_tax_after_credits: -3288.06,
					total_payroll_tax: 7956,
					cash_income: 55978,
					tax_burden: 689.94,
					tax_wedge: 4667.9400000000005,
					take_home_pay: 51310.06,
					avg_effective_tax_rate: 0.0833888313265926,
					avg_effective_tax_rate_wo_payroll: -0.06323192307692307,
					marginal_income_tax_rate: 0.4071,
					marginal_business_income_tax_rate: 0.22881949999999998
				}
			}
		]
	},
	{
		description: {
			name: 'Amber',
			filingData: 'Single, No Kids',
			id: 'amber',
			income: 75000,
			tooltip: 'This taxpayer takes the standard deduction and had $5,500 of pre-tax 401(k) contributions. Amber earned $10,000 in qualified tips.'
		},
		taxes: [
			{
				plan: {
					id: 'current',
					name: 'Pre-OBBBA Law (TCJA Expires)',
					shortName: 'Pre-OBBBA (TCJA Expires)',
					year: 2026
				},
				results: {
					gross_income: 75000,
					employee_payroll_tax: 5737.5,
					employer_payroll_tax: 5737.5,
					ordinary_income_after_401k: 69500,
					agi: 69500,
					taxable_income: 55900,
					deduction_type: 'standard',
					deductions: 8300,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 8300,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 5300,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 8437.5,
					qualified_income_tax: 8437.5,
					selected_tax_before_credits: 8437.5,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 8437.5,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 8437.5,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 8437.5,
					income_tax_after_credits: 8437.5,
					total_payroll_tax: 11475,
					cash_income: 80737.5,
					tax_burden: 14175,
					tax_wedge: 19912.5,
					take_home_pay: 60825,
					avg_effective_tax_rate: 0.24663260566651185,
					avg_effective_tax_rate_wo_payroll: 0.1125,
					marginal_income_tax_rate: 0.3265,
					marginal_business_income_tax_rate: 0.37363550000000034
				}
			},
			{
				plan: {
					id: 'OBBBA_2026',
					name: 'One Big Beautiful Bill Act',
					shortName: 'OBBBA',
					year: 2026
				},
				results: {
					gross_income: 75000,
					employee_payroll_tax: 5737.5,
					employer_payroll_tax: 5737.5,
					ordinary_income_after_401k: 69500,
					agi: 69500,
					taxable_income: 43400,
					deduction_type: 'standard',
					deductions: 26100,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 10000,
					standard_ded_for_display: 16100,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 0,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 4960,
					qualified_income_tax: 4960,
					selected_tax_before_credits: 4960,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 4960,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 4960,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 4960,
					income_tax_after_credits: 4960,
					total_payroll_tax: 11475,
					cash_income: 80737.5,
					tax_burden: 10697.5,
					tax_wedge: 16435,
					take_home_pay: 64302.5,
					avg_effective_tax_rate: 0.20356092274345874,
					avg_effective_tax_rate_wo_payroll: 0.06613333333333334,
					marginal_income_tax_rate: 0.1965,
					marginal_business_income_tax_rate: 0.2288195000000007
				}
			}
		]
	},
	{
		description: {
			name: 'Kavya & Nick',
			filingData: 'Married, 2 Kids',
			id: 'kavya',
			income: 85000,
			tooltip: 'These taxpayers take the standard deduction and had $5,500 of pre-tax 401(k) contributions.'
		},
		taxes: [
			{
				plan: {
					id: 'current',
					name: 'Pre-OBBBA Law (TCJA Expires)',
					shortName: 'Pre-OBBBA (TCJA Expires)',
					year: 2026
				},
				results: {
					gross_income: 85000,
					employee_payroll_tax: 6502.5,
					employer_payroll_tax: 6502.5,
					ordinary_income_after_401k: 79500,
					agi: 79500,
					taxable_income: 41700,
					deduction_type: 'standard',
					deductions: 16600,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 16600,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 21200,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 5040,
					qualified_income_tax: 5040,
					selected_tax_before_credits: 5040,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 5040,
					ctc: 2000,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 3040,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 3040,
					income_tax_after_credits: 3040,
					total_payroll_tax: 13005,
					cash_income: 91502.5,
					tax_burden: 9542.5,
					tax_wedge: 16045,
					take_home_pay: 75457.5,
					avg_effective_tax_rate: 0.1753504002622879,
					avg_effective_tax_rate_wo_payroll: 0.03576470588235294,
					marginal_income_tax_rate: 0.2265,
					marginal_business_income_tax_rate: 0.28069950000000027
				}
			},
			{
				plan: {
					id: 'OBBBA_2026',
					name: 'One Big Beautiful Bill Act',
					shortName: 'OBBBA',
					year: 2026
				},
				results: {
					gross_income: 85000,
					employee_payroll_tax: 6502.5,
					employer_payroll_tax: 6502.5,
					ordinary_income_after_401k: 79500,
					agi: 79500,
					taxable_income: 43800,
					deduction_type: 'standard',
					deductions: 35700,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 3500,
					tips_income_ded: 0,
					standard_ded_for_display: 32200,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 0,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 4760,
					qualified_income_tax: 4760,
					selected_tax_before_credits: 4760,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 4760,
					ctc: 4400,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 360,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 360,
					income_tax_after_credits: 360,
					total_payroll_tax: 13005,
					cash_income: 91502.5,
					tax_burden: 6862.5,
					tax_wedge: 13365,
					take_home_pay: 78137.5,
					avg_effective_tax_rate: 0.14606158301685745,
					avg_effective_tax_rate_wo_payroll: 0.0042352941176470585,
					marginal_income_tax_rate: 0.1965,
					marginal_business_income_tax_rate: 0.22881949999999998
				}
			}
		]
	},
	{
		description: {
			name: 'Sophie & Chad',
			filingData: 'Married, 1 Retired, 2 Kids',
			id: 'sophie',
			income: 165000,
			tooltip: 'These taxpayers claimed about $26,000 worth of itemized deductions and had $20,000 of pre-tax 401(k) contributions. Chad is over 65 years old'
		},
		taxes: [
			{
				plan: {
					id: 'current',
					name: 'Pre-OBBBA Law (TCJA Expires)',
					shortName: 'Pre-OBBBA (TCJA Expires)',
					year: 2026
				},
				results: {
					gross_income: 165000,
					employee_payroll_tax: 12622.5,
					employer_payroll_tax: 12622.5,
					ordinary_income_after_401k: 145000,
					agi: 145000,
					taxable_income: 98060,
					deduction_type: 'itemized',
					deductions: 25740,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 0,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 8500,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 11650,
					mortgage_int_ded_for_display: 5590,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 21200,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 13494,
					qualified_income_tax: 13494,
					selected_tax_before_credits: 13494,
					amt_taxable_income: 21110,
					amt: 0,
					income_tax_before_credits_with_amt: 13494,
					ctc: 250,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 13244,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 13244,
					income_tax_after_credits: 13244,
					total_payroll_tax: 25245,
					cash_income: 177622.5,
					tax_burden: 25866.5,
					tax_wedge: 38489,
					take_home_pay: 139133.5,
					avg_effective_tax_rate: 0.21668989007586315,
					avg_effective_tax_rate_wo_payroll: 0.08026666666666667,
					marginal_income_tax_rate: 0.3649,
					marginal_business_income_tax_rate: 0.4120354999999996
				}
			},
			{
				plan: {
					id: 'OBBBA_2026',
					name: 'One Big Beautiful Bill Act',
					shortName: 'OBBBA',
					year: 2026
				},
				results: {
					gross_income: 165000,
					employee_payroll_tax: 12622.5,
					employer_payroll_tax: 12622.5,
					ordinary_income_after_401k: 145000,
					agi: 145000,
					taxable_income: 104800,
					deduction_type: 'standard',
					deductions: 40200,
					senior_ded: 6000,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 32200,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 2000,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 0,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 12480,
					qualified_income_tax: 12480,
					selected_tax_before_credits: 12480,
					amt_taxable_income: 4800,
					amt: 0,
					income_tax_before_credits_with_amt: 12480,
					ctc: 4400,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 8080,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 8080,
					income_tax_after_credits: 8080,
					total_payroll_tax: 25245,
					cash_income: 177622.5,
					tax_burden: 20702.5,
					tax_wedge: 33325,
					take_home_pay: 144297.5,
					avg_effective_tax_rate: 0.18761699672057314,
					avg_effective_tax_rate_wo_payroll: 0.04896969696969697,
					marginal_income_tax_rate: 0.2965,
					marginal_business_income_tax_rate: 0.30175149999999995
				}
			}
		]
	},
	{
		description: {
			name: 'Soren & Linea',
			filingData: 'Married, 3 Kids',
			id: 'soren',
			income: 200000,
			tooltip: 'These taxpayers earned half their income via a pass-through business which qualifies for the new 20-percent deduction and claimed about $30,000 worth of other itemized deductions. They also had $19,000 of pre-tax 401(k) contributions.'
		},
		taxes: [
			{
				plan: {
					id: 'current',
					name: 'Pre-OBBBA Law (TCJA Expires)',
					shortName: 'Pre-OBBBA (TCJA Expires)',
					year: 2026
				},
				results: {
					gross_income: 200000,
					employee_payroll_tax: 7650,
					employer_payroll_tax: 7650,
					ordinary_income_after_401k: 81000,
					agi: 173935.225,
					taxable_income: 117095.225,
					deduction_type: 'itemized',
					deductions: 30340,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 0,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 8500,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 16250,
					mortgage_int_ded_for_display: 5590,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 26500,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 18198.81,
					qualified_income_tax: 18198.81,
					selected_tax_before_credits: 18198.81,
					amt_taxable_income: 50045.225000000006,
					amt: 0,
					income_tax_before_credits_with_amt: 18198.81,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 18198.81,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 14129.55,
					income_tax_after_other_taxes: 18198.81,
					income_tax_after_credits: 18198.81,
					total_payroll_tax: 29429.55,
					cash_income: 207650,
					tax_burden: 39978.36,
					tax_wedge: 47628.36,
					take_home_pay: 160021.64,
					avg_effective_tax_rate: 0.2293684565374428,
					avg_effective_tax_rate_wo_payroll: 0.09099405,
					marginal_income_tax_rate: 0.3265,
					marginal_business_income_tax_rate: 0.3736315000000002
				}
			},
			{
				plan: {
					id: 'OBBBA_2026',
					name: 'One Big Beautiful Bill Act',
					shortName: 'OBBBA',
					year: 2026
				},
				results: {
					gross_income: 200000,
					employee_payroll_tax: 7650,
					employer_payroll_tax: 7650,
					ordinary_income_after_401k: 81000,
					agi: 173935.225,
					taxable_income: 119735.225,
					deduction_type: 'standard',
					deductions: 54200,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 32200,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 2000,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 0,
					pease_limitation_amt: 0,
					qbi_ded: 20000,
					income_tax_before_credits: 15765.75,
					qualified_income_tax: 15765.75,
					selected_tax_before_credits: 15765.75,
					amt_taxable_income: 33735.225000000006,
					amt: 0,
					income_tax_before_credits_with_amt: 15765.75,
					ctc: 6600,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 9165.75,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 14129.55,
					income_tax_after_other_taxes: 9165.75,
					income_tax_after_credits: 9165.75,
					total_payroll_tax: 29429.55,
					cash_income: 207650,
					tax_burden: 30945.3,
					tax_wedge: 38595.3,
					take_home_pay: 169054.7,
					avg_effective_tax_rate: 0.1858670840356369,
					avg_effective_tax_rate_wo_payroll: 0.04582875,
					marginal_income_tax_rate: 0.2965,
					marginal_business_income_tax_rate: 0.30175149999999995
				}
			}
		]
	},
	{
		description: {
			name: 'Laura & Seth',
			filingData: 'Married, 2 Kids',
			id: 'laura',
			income: 2000000,
			tooltip: 'These taxpayers claimed about $220,000 worth of itemized deductions and had $18,500 of pre-tax 401(k) contributions.'
		},
		taxes: [
			{
				plan: {
					id: 'current',
					name: 'Pre-OBBBA Law (TCJA Expires)',
					shortName: 'Pre-OBBBA (TCJA Expires)',
					year: 2026
				},
				results: {
					gross_income: 2000000,
					employee_payroll_tax: 40532,
					employer_payroll_tax: 40532,
					ordinary_income_after_401k: 1981500,
					agi: 1981500,
					taxable_income: 1806356.5,
					deduction_type: 'itemized',
					deductions: 175143.5,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 0,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 50000,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 131250,
					mortgage_int_ded_for_display: 35000,
					other_itemized_ded_for_display: 6103,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: -47209.5,
					personal_exemption_amt: 0,
					pease_limitation_amt: 47209.5,
					qbi_ded: 0,
					income_tax_before_credits: 644138.17,
					qualified_income_tax: 644138.17,
					selected_tax_before_credits: 644138.17,
					amt_taxable_income: 1896500,
					amt: 0,
					income_tax_before_credits_with_amt: 644138.17,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 644138.17,
					medicare_surtax: 15749.999999999998,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 659888.17,
					income_tax_after_credits: 659888.17,
					total_payroll_tax: 81064,
					cash_income: 2040532,
					tax_burden: 700420.17,
					tax_wedge: 740952.17,
					take_home_pay: 1299579.83,
					avg_effective_tax_rate: 0.36311715278172557,
					avg_effective_tax_rate_wo_payroll: 0.329944085,
					marginal_income_tax_rate: 0.4313799999999814,
					marginal_business_income_tax_rate: 0.4747824999999721
				}
			},
			{
				plan: {
					id: 'OBBBA_2026',
					name: 'One Big Beautiful Bill Act',
					shortName: 'OBBBA',
					year: 2026
				},
				results: {
					gross_income: 2000000,
					employee_payroll_tax: 40532,
					employer_payroll_tax: 40532,
					ordinary_income_after_401k: 1981500,
					agi: 1981500,
					taxable_income: 1899963.366775,
					deduction_type: 'itemized',
					deductions: 81536.633225,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 0,
					additional_standard_deduction: 0,
					charitable_ded_for_display: 40092.5,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 10000,
					mortgage_int_ded_for_display: 30000,
					other_itemized_ded_for_display: 6103,
					itemized_deductions_haircut: -4658.866775000002,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 0,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 625150.95,
					qualified_income_tax: 625150.95,
					selected_tax_before_credits: 625150.95,
					amt_taxable_income: 2037316.366775,
					amt: 0,
					income_tax_before_credits_with_amt: 625150.95,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 625150.95,
					medicare_surtax: 15749.999999999998,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 640900.95,
					income_tax_after_credits: 640900.95,
					total_payroll_tax: 81064,
					cash_income: 2040532,
					tax_burden: 681432.95,
					tax_wedge: 721964.95,
					take_home_pay: 1318567.05,
					avg_effective_tax_rate: 0.35381211860436396,
					avg_effective_tax_rate_wo_payroll: 0.32045047499999996,
					marginal_income_tax_rate: 0.39524799999999816,
					marginal_business_income_tax_rate: 0.3661665000000037
				}
			}
		]
	},
	{
		description: {
			name: 'James & Eddie',
			filingData: 'Married, Retired',
			id: 'joe',
			income: 48000,
			tooltip: 'These taxpayers takes the standard deduction and had $2,600 of pre-tax 401(k) contributions. They are both over 65 years old.'
		},
		taxes: [
			{
				plan: {
					id: 'current',
					name: 'Pre-OBBBA Law (TCJA Expires)',
					shortName: 'Pre-OBBBA (TCJA Expires)',
					year: 2026
				},
				results: {
					gross_income: 48000,
					employee_payroll_tax: 2386.8,
					employer_payroll_tax: 2386.8,
					ordinary_income_after_401k: 31200,
					agi: 35000,
					taxable_income: 4500,
					deduction_type: 'standard',
					deductions: 19900,
					senior_ded: 0,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 16600,
					additional_standard_deduction: 3300,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 10600,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 450,
					qualified_income_tax: 450,
					selected_tax_before_credits: 450,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 450,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 450,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 450,
					income_tax_after_credits: 450,
					total_payroll_tax: 4773.6,
					cash_income: 50386.8,
					tax_burden: 2836.8,
					tax_wedge: 5223.6,
					take_home_pay: 45163.200000000004,
					avg_effective_tax_rate: 0.10367000881183167,
					avg_effective_tax_rate_wo_payroll: 0.009375,
					marginal_income_tax_rate: 0.2265,
					marginal_business_income_tax_rate: 0.2806995000000001
				}
			},
			{
				plan: {
					id: 'OBBBA_2026',
					name: 'One Big Beautiful Bill Act',
					shortName: 'OBBBA',
					year: 2026
				},
				results: {
					gross_income: 48000,
					employee_payroll_tax: 2386.8,
					employer_payroll_tax: 2386.8,
					ordinary_income_after_401k: 31200,
					agi: 35000,
					taxable_income: 0,
					deduction_type: 'standard',
					deductions: 47500,
					senior_ded: 12000,
					ot_income_ded: 0,
					auto_loan_interest_ded: 0,
					tips_income_ded: 0,
					standard_ded_for_display: 32200,
					additional_standard_deduction: 3300,
					charitable_ded_for_display: 0,
					med_exp_ded_for_display: 0,
					salt_ded_for_display: 0,
					mortgage_int_ded_for_display: 0,
					other_itemized_ded_for_display: 0,
					itemized_deductions_haircut: 0,
					pease_limitation_for_display: 0,
					personal_exemption_amt: 0,
					pease_limitation_amt: 0,
					qbi_ded: 0,
					income_tax_before_credits: 0,
					qualified_income_tax: 0,
					selected_tax_before_credits: 0,
					amt_taxable_income: 0,
					amt: 0,
					income_tax_before_credits_with_amt: 0,
					ctc: 0,
					actc: 0,
					eitc: 0,
					dep_credit: 0,
					income_tax_after_nonrefundable_credits: 0,
					medicare_surtax: 0,
					niit: 0,
					sched_se_tax: 0,
					income_tax_after_other_taxes: 0,
					income_tax_after_credits: 0,
					total_payroll_tax: 4773.6,
					cash_income: 50386.8,
					tax_burden: 2386.8,
					tax_wedge: 4773.6,
					take_home_pay: 45613.200000000004,
					avg_effective_tax_rate: 0.09473909833527829,
					avg_effective_tax_rate_wo_payroll: 0,
					marginal_income_tax_rate: 0.0765,
					marginal_business_income_tax_rate: 0.1412955
				}
			}
		]
	}
] as const satisfies Profile[];

export default profiles;

export type ProfileId = (typeof profiles)[number]['description']['id'];

export type ProfileOption = ProfileId | 'custom';
